import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import { createTruckType, listAdminTruckTypes, updateTruckType } from '../../api/endpoints/truckTypes';
import { DnmRequestFailure, TRUCK_TYPE_FIELD_COPY } from '../../api/dnmFailure';
import { referenceQueryPolicy } from '../../api/queryPolicies';
import type { AdminTruckType, CreateTruckTypeRequest, TruckTypeField, UpdateTruckTypeRequest } from '../../api/types';
import { TRUCK_CATALOG } from '../../config/truckCatalog';
import { TRUCK_CATALOG_QUERY_KEY } from '../../hooks/useTruckCatalog';
import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { DialogShell } from '../../components/ui/DialogShell';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { SectionCard } from '../../components/ui/SectionCard';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import formStyles from '../../components/domain/FormModal.module.css';
import styles from './TruckTypesTab.module.css';
import { ViewOnlyNote } from '../../components/ui/ViewOnlyNote';
import { useOpsUser } from '../ownership/opsUser';
import { DialogHelp } from '../../help/DialogHelp';

/**
 * Settings → Truck types (#249): the truck catalog every picker loads. Grouped by the four fixed
 * categories, searchable, Add / Edit / Hide / Show. There is no delete: hidden types stay on the
 * shipments and lanes that use them (#249 Contract sections 1 and 3).
 */

const ADMIN_KEY = ['admin', 'truck-types'] as const;
/** Categories stay fixed in code (Contract section 1); the bundled copy carries their order and names. */
const CATEGORIES = TRUCK_CATALOG.map((c) => ({ id: c.id, name: c.name }));

const STALE = 'Someone else changed this truck type. Reload and try again.';
const NOT_FOUND = 'This truck type no longer exists. Reload the list.';

/** Case-, space- and punctuation-forgiving search key (UX principle 2): "taurus10" finds "Taurus (10/12 …)". */
const searchKey = (v: string | null | undefined) => (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

export function TruckTypesTab() {
  const { t } = useTranslation();
  // Add / Edit / Hide / Show are OPS_MANAGER only (R67); executives see the catalog read-only.
  const { isManager } = useOpsUser();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ row: AdminTruckType | null; categoryId: string } | null>(null);
  const [hiding, setHiding] = useState<AdminTruckType | null>(null);
  const [hideError, setHideError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ADMIN_KEY,
    queryFn: ({ signal }) => listAdminTruckTypes(signal),
    ...referenceQueryPolicy,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
    void queryClient.invalidateQueries({ queryKey: TRUCK_CATALOG_QUERY_KEY });
  };

  const visibility = useMutation({
    mutationFn: (row: AdminTruckType) => updateTruckType(row.id, { active: !row.active, version: row.version }),
    onSuccess: (saved) => {
      toast.success(saved.active ? `${saved.name} is shown in pickers again.` : `${saved.name} is hidden from pickers.`);
      setHiding(null);
      invalidate();
    },
    onError: (err, row) => {
      invalidate(); // a stale version is refreshed for the next try
      const message = err instanceof Error ? err.message : STALE;
      if (row.active) setHideError(message);
      else toast.error(message);
    },
  });

  const q = searchKey(search);
  const rows = useMemo(
    () => (data ?? []).filter((r) => !q || [r.name, r.id, r.idealFor].some((v) => searchKey(v).includes(q))),
    [data, q],
  );

  const allColumns: Column<AdminTruckType>[] = [
    {
      key: 'name',
      header: 'Name',
      width: '25%',
      render: (r) => <span className={r.active ? styles.name : `${styles.name} ${styles.hidden}`}>{r.name}</span>,
    },
    { key: 'capacity', header: 'Capacity (t)', width: '12%', render: (r) => <Dim row={r} value={r.capacityTons} nowrap /> },
    { key: 'length', header: 'Length (ft)', width: '11%', render: (r) => <Dim row={r} value={r.lengthFt} nowrap /> },
    { key: 'idealFor', header: 'Ideal for', render: (r) => <Dim row={r} value={r.idealFor} /> },
    {
      key: 'status',
      header: 'Status',
      width: '12%',
      render: (r) =>
        r.active ? (
          <StatusPill bg="var(--status-green-bg)" text="var(--status-green-text)" label={t('settings.truckTypes.statusActive')} icon={<StatusIcon name="check" />} />
        ) : (
          <StatusPill bg="var(--status-gray-bg)" text="var(--status-gray-text)" label={t('settings.truckTypes.statusHidden')} icon={<StatusIcon name="close" />} />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '19%',
      render: (r) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" size="sm" onClick={() => setEditing({ row: r, categoryId: r.categoryId })}>
            {t('settings.truckTypes.edit')}
          </Button>
          {r.active ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setHideError(null);
                setHiding(r);
              }}
            >
              {t('settings.truckTypes.hide')}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              busy={visibility.isPending && visibility.variables?.id === r.id}
              onClick={() => visibility.mutate(r)}
            >
              {t('settings.truckTypes.show')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const columns = isManager ? allColumns : allColumns.filter((c) => c.key !== 'actions');

  const shownCategories = CATEGORIES.filter((c) => !q || rows.some((r) => r.categoryId === c.id));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.search}
          aria-label={t('settings.truckTypes.searchLabel')}
          placeholder={t('settings.truckTypes.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <RefreshButton queryKeys={[[...ADMIN_KEY]]} />
        {isManager && (
          <Button variant="primary" onClick={() => setEditing({ row: null, categoryId: CATEGORIES[0].id })}>
            {t('settings.truckTypes.addTruckType')}
          </Button>
        )}
      </div>
      {!isManager && <ViewOnlyNote />}

      {isLoading && !data && <LoadingState label={t('settings.truckTypes.loading')} />}
      {isError &&
        (data ? (
          <StaleBanner onRetry={() => refetch()} />
        ) : (
          <ErrorState title={t('settings.truckTypes.loadError')} onRetry={() => refetch()} />
        ))}

      {data && q && rows.length === 0 && (
        <div className={styles.emptySearch}>
          <p>{t('settings.truckTypes.noMatch', { query: search.trim() })}</p>
          <Button variant="secondary" size="sm" onClick={() => setSearch('')}>
            {t('settings.truckTypes.clearSearch')}
          </Button>
        </div>
      )}

      {data &&
        shownCategories.map((category) => {
          const inCategory = rows.filter((r) => r.categoryId === category.id);
          return (
            <SectionCard key={category.id} title={category.name}>
              {inCategory.length === 0 ? (
                <div className={styles.emptyCategory}>
                  <p>{t('settings.truckTypes.emptyCategory')}</p>
                  {isManager && (
                    <Button variant="secondary" size="sm" onClick={() => setEditing({ row: null, categoryId: category.id })}>
                      {t('settings.truckTypes.add')}
                    </Button>
                  )}
                </div>
              ) : (
                <DataTable columns={columns} rows={inCategory} keyFn={(r) => r.id} />
              )}
            </SectionCard>
          );
        })}

      {isManager && editing && (
        <TruckTypeDialog row={editing.row} categoryId={editing.categoryId} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}

      <ConfirmDialog
        isOpen={isManager && hiding != null}
        title={hiding ? `Hide ${hiding.name}?` : ''}
        description="It disappears from pickers. Shipments and lanes that use it keep it."
        confirmText="Hide"
        variant="danger"
        loading={visibility.isPending}
        errorMessage={hideError}
        onConfirm={() => hiding && visibility.mutate(hiding)}
        onCancel={() => setHiding(null)}
        help="truck-type"
      />
    </div>
  );
}

function Dim({ row, value, nowrap }: { row: AdminTruckType; value: string | null; nowrap?: boolean }) {
  const cls = [row.active ? '' : styles.hidden, nowrap ? styles.nowrap : ''].filter(Boolean).join(' ');
  return <span className={cls || undefined}>{value?.trim() || '—'}</span>;
}

// ─── Add / Edit dialog ────────────────────────────────────────────────────────

interface FormValues {
  categoryId: string;
  name: string;
  capacityTons: string;
  lengthFt: string;
  idealFor: string;
  sortOrder: string;
}

const TEXT_LIMITS = { capacityTons: 30, lengthFt: 30, idealFor: 200 } as const;
const TEXT_FIELDS = Object.keys(TEXT_LIMITS) as (keyof typeof TEXT_LIMITS)[];

function valuesOf(row: AdminTruckType | null, categoryId: string): FormValues {
  return {
    categoryId: row?.categoryId ?? categoryId,
    name: row?.name ?? '',
    capacityTons: row?.capacityTons ?? '',
    lengthFt: row?.lengthFt ?? '',
    idealFor: row?.idealFor ?? '',
    sortOrder: row ? String(row.sortOrder) : '',
  };
}

/** Client checks mirror the Contract (section 3) so most mistakes never reach the server. */
function validate(v: FormValues): Partial<Record<TruckTypeField, string>> {
  const errors: Partial<Record<TruckTypeField, string>> = {};
  if (!CATEGORIES.some((c) => c.id === v.categoryId)) errors.categoryId = TRUCK_TYPE_FIELD_COPY.categoryId;
  const name = v.name.trim();
  if (name.length < 2 || name.length > 60) errors.name = TRUCK_TYPE_FIELD_COPY.name;
  for (const f of TEXT_FIELDS) {
    if (v[f].trim().length > TEXT_LIMITS[f]) errors[f] = TRUCK_TYPE_FIELD_COPY[f];
  }
  if (v.sortOrder.trim() && !/^-?\d+$/.test(v.sortOrder.trim())) errors.sortOrder = TRUCK_TYPE_FIELD_COPY.sortOrder;
  return errors;
}

const blankToNull = (v: string) => v.trim() || null;

function createBody(v: FormValues): CreateTruckTypeRequest {
  return {
    categoryId: v.categoryId,
    name: v.name.trim(),
    capacityTons: blankToNull(v.capacityTons),
    lengthFt: blankToNull(v.lengthFt),
    idealFor: blankToNull(v.idealFor),
    ...(v.sortOrder.trim() ? { sortOrder: Number(v.sortOrder.trim()) } : {}),
  };
}

/** PATCH carries only what changed, plus the row version (Contract section 3). */
function patchBody(row: AdminTruckType, v: FormValues, version: number): UpdateTruckTypeRequest {
  const changes: Partial<CreateTruckTypeRequest> = {};
  if (v.categoryId !== row.categoryId) changes.categoryId = v.categoryId;
  if (v.name.trim() !== row.name) changes.name = v.name.trim();
  for (const f of TEXT_FIELDS) {
    if (blankToNull(v[f]) !== (row[f] ?? null)) changes[f] = blankToNull(v[f]);
  }
  if (v.sortOrder.trim() && Number(v.sortOrder.trim()) !== row.sortOrder) changes.sortOrder = Number(v.sortOrder.trim());
  return { ...changes, version };
}

function TruckTypeDialog({
  row,
  categoryId,
  onClose,
  onSaved,
}: {
  row: AdminTruckType | null;
  categoryId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = row != null;
  const [values, setValues] = useState<FormValues>(() => valuesOf(row, categoryId));
  const [version, setVersion] = useState(row?.version ?? 0);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<TruckTypeField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [reloading, setReloading] = useState(false);

  const save = useMutation({
    mutationFn: () => (row ? updateTruckType(row.id, patchBody(row, values, version)) : createTruckType(createBody(values))),
    onSuccess: (saved) => {
      toast.success(isEdit ? `${saved.name} saved.` : `${saved.name} added.`);
      onSaved();
      onClose();
    },
    onError: (err) => {
      // The user's input is always kept; the plain sentence lands where it belongs.
      const failure = err instanceof DnmRequestFailure ? err : null;
      if (failure?.code === 'TRUCK_TYPE_STALE') {
        setStale(true);
        setFormError(STALE);
      } else if (failure?.code === 'TRUCK_TYPE_NAME_TAKEN') {
        setFieldErrors({ name: failure.message });
      } else if (failure?.field) {
        setFieldErrors({ [failure.field]: failure.message });
      } else if (failure?.code === 'TRUCK_CATEGORY_UNKNOWN') {
        setFieldErrors({ categoryId: failure.message });
      } else {
        setFormError(err instanceof Error ? err.message : 'Check the entered information and try again.');
      }
    },
  });

  const dirty = useIsDirty({ ...values });
  const dismiss = useDialogDismiss({ dirty, busy: save.isPending, onClose });

  const set = (key: keyof FormValues) => (value: string) => {
    setValues((v) => ({ ...v, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: undefined }));
  };

  /** Reload after a 409 STALE: take the row's latest version, keep everything the user typed. */
  const reload = async () => {
    if (!row) return;
    setReloading(true);
    try {
      const latest = (await listAdminTruckTypes()).find((r) => r.id === row.id);
      if (!latest) {
        setFormError(NOT_FOUND);
        return;
      }
      setVersion(latest.version);
      setStale(false);
      setFormError(null);
      onSaved();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : STALE);
    } finally {
      setReloading(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errors = validate(values);
    setFieldErrors(errors);
    setFormError(null);
    if (Object.values(errors).some(Boolean)) return;
    save.mutate();
  };

  const title = isEdit ? 'Edit truck type' : 'Add truck type';
  const field = (id: TruckTypeField, label: string, input: (props: { id: string; invalid: boolean }) => ReactNode) => (
    <>
      <label className={formStyles.label} htmlFor={`truck-type-${id}`}>
        {label}
      </label>
      {input({ id: `truck-type-${id}`, invalid: !!fieldErrors[id] })}
      {fieldErrors[id] && (
        <p id={`truck-type-${id}-error`} className={styles.fieldError}>
          {fieldErrors[id]}
        </p>
      )}
    </>
  );
  const text = (id: Exclude<TruckTypeField, 'categoryId'>, label: string, placeholder: string, maxLength?: number) =>
    field(id, label, ({ id: inputId, invalid }) => (
      <input
        id={inputId}
        className={formStyles.input}
        value={values[id]}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${inputId}-error` : undefined}
        onChange={(e) => set(id)(e.target.value)}
      />
    ));

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={formStyles.scrim} panelClassName={formStyles.panel}>
      <div className={formStyles.titleRow}>
        <h2 className={formStyles.title}>{title}</h2>
        <DialogHelp context="truck-type" title={title} />
      </div>
      <form onSubmit={submit} noValidate>
        {field('categoryId', 'Category', ({ id, invalid }) => (
          <select
            id={id}
            className={formStyles.input}
            value={values.categoryId}
            aria-invalid={invalid || undefined}
            onChange={(e) => set('categoryId')(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ))}
        {text('name', 'Name', 'e.g. 14 ft Canter', 60)}
        <div className={styles.pair}>
          <div>{text('capacityTons', 'Capacity (t)', 'e.g. 3 - 4', 30)}</div>
          <div>{text('lengthFt', 'Length (ft)', 'e.g. 14', 30)}</div>
        </div>
        {text('idealFor', 'Ideal for', 'e.g. City deliveries, FMCG', 200)}
        {text('sortOrder', 'Order in list', 'Last in its category')}

        {formError && (
          <div className={formStyles.error} role="alert">
            {formError}
            {stale && (
              <div className={styles.reload}>
                <Button variant="secondary" size="sm" busy={reloading} onClick={() => void reload()}>
                  {t('settings.truckTypes.reload')}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className={styles.dialogActions}>
          <Button variant="ghost" onClick={dismiss.requestClose} disabled={save.isPending}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" busy={save.isPending} disabled={stale}>
            {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add truck type'}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
